import { DataTable } from "@/components/data-table";
import { StatusMessage } from "@/components/status-message";
import {
	useGlobalPriors,
	usePriorMutations,
} from "@/features/plausibility/use-priors";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";

import { priorColumns } from "./prior-columns";
import { PriorForm } from "./prior-form";
import { SeedPanel } from "./SeedPanel";
import { usePriorRowEdit } from "./use-prior-row-edit";

/**
 * Global prior odds: the baseline every organization inherits, in the admin
 * database. Entered as a probability and stored as log odds. Scoped overrides
 * for one organization live in that organization's Plausibility tab.
 */
export const GlobalPriors = () => {
	const taxonomy = useTaxonomy();
	const classes = (taxonomy.data ?? []).filter((cls) => cls.alertable);
	const priors = useGlobalPriors();
	const { deleteGlobal, upsertGlobal } = usePriorMutations();
	const busy = deleteGlobal.isPending || upsertGlobal.isPending;
	const rows = priors.data ?? [];
	const { edit, startEdit } = usePriorRowEdit((_row, input) =>
		upsertGlobal.mutateAsync(input),
	);

	return (
		<section>
			<div>
				<PriorForm
					busy={busy}
					classes={classes}
					error={edit ? null : upsertGlobal.error}
					onSubmit={(input) => upsertGlobal.mutate(input)}
				/>
			</div>
			<div className="mt-4">
				<StatusMessage
					empty="No global priors. Seed them below."
					error={priors.error}
					isEmpty={!priors.isLoading && rows.length === 0}
					isLoading={priors.isLoading}
					loading="Loading global priors…"
				/>
				{edit && upsertGlobal.error && (
					<p className="mb-2 text-sm text-error-primary" role="alert">
						Could not save. {upsertGlobal.error.message}
					</p>
				)}
				{rows.length > 0 && (
					<DataTable
						columns={priorColumns({
							busy,
							edit,
							onDelete: (id) => deleteGlobal.mutate(id),
							onEdit: startEdit,
						})}
						rowKey={(row) => row.id}
						rows={rows}
					/>
				)}
			</div>
			<div className="mt-8">
				<SeedPanel />
			</div>
		</section>
	);
};
